import React, { useCallback, useEffect, useState } from 'react'
import { Box, CircleButton, Flex, Text } from '@revolut/ui-kit'
import * as Icons from '@revolut/icons'

import styled from 'styled-components'

import { Slide } from '../../components/Slide'
import { useKeyDown } from '../../hooks/useKeyPress'
import { Keys } from '../../constants/common'
import { PasswordError } from '../../thunks'
import { useTimedPasswordError } from '../../hooks/useTimedPasswordError'

type PinCodePropTypes = {
  handleSubmit: () => void
  setFieldValue: (field: string, value?: string) => void
  onBack: (...args: any[]) => void
  errors: {
    password?: PasswordError
  }
  values: {
    phone: {
      code: string
      number: string
    }
  }
}

const PinIndicator = styled(Box).attrs<{ hasValue: boolean }>(({ hasValue }) => ({
  width: '.75rem',
  height: '.75rem',
  radius: '50%',
  bg: hasValue ? 'primary' : 'grey-80',
}))<{ hasValue: boolean }>`
  opacity: ${({ hasValue }) => (hasValue ? 1 : 0.6)};
`

const DialButton = styled(CircleButton).attrs({
  width: '4rem',
  elevation: 0,
  mx: { all: '.5rem', md: '1rem' },
  elevationOnHover: 300,
})`
  height: 4rem;
  & :first-child {
    margin-left: 0;
  }
  & :last-child {
    margin-right: 0;
  }
`

const ErrorShaker = styled(Box)<{ shake: boolean }>`
  animation-name: ${(props) => (props.shake ? 'shake' : 'none')};
  animation-duration: 0.4s;
  animation-timing-function: linear;
`

const BACK_BUTTON_PLACEHOLDER = '<'

const BUTTONS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['', '0', BACK_BUTTON_PLACEHOLDER],
]

const KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', Keys.BACKSPACE]

const PIN_LENGTH = 4

export const PinCode: React.FC<PinCodePropTypes> = ({
  handleSubmit,
  setFieldValue,
  errors,
  values,
  onBack,
  ...rest
}) => {
  const [pinCode, setPinCode] = useState('')
  const [pinCodeEntered, setPincodeEntered] = useState(false)
  const [buttonsDisabled, setButtonsDisabled] = useState(false)
  const timePassedCallback = useCallback(() => {
    setFieldValue('password', '')
    setPinCode('')
  }, [setFieldValue])
  const { timedPasswordMessage, hasTimedPasswordError } = useTimedPasswordError({
    errors,
    timePassedCallback,
  })

  const appendPinCode = useCallback((append) => {
    setPinCode((prevState) => prevState + append)
  }, [])
  const removePinCodeSymbol = useCallback(() => {
    setPinCode((prevState) => prevState.slice(0, prevState.length - 1))
  }, [])

  useKeyDown(
    KEYS,
    (event: Event) => {
      const key = (event as KeyboardEvent).key
      key === Keys.BACKSPACE ? removePinCodeSymbol() : appendPinCode(key)
    },
    [],
  )
  useEffect(() => {
    setFieldValue('password', pinCode)
    if (pinCode.length === PIN_LENGTH) {
      setPincodeEntered(true)
    }
  }, [pinCode, setFieldValue])

  useEffect(() => {
    // Effect and state is needed since 'setFieldValue' has inner async logic
    // so 'handleSubmit' will use previous value instead of just updated
    if (pinCodeEntered) {
      setButtonsDisabled(true)
      handleSubmit()
      setPincodeEntered(false)
    }
  }, [handleSubmit, pinCodeEntered])

  useEffect(() => {
    setButtonsDisabled(hasTimedPasswordError)
  }, [errors.password, hasTimedPasswordError])

  return (
    <Slide
      title='Enter your Revolut app passcode'
      subtitle={`Please confirm that you own the account associated with number ${values.phone.code} ${values.phone.number}`}
      onBack={(...args: any[]) => {
        setFieldValue('password', '')
        onBack(...args)
      }}
      {...rest}
    >
      <Flex flexDirection='column' mt='2rem'>
        {hasTimedPasswordError ? (
          <Text fontSize='.875rem' color='accent'>
            {timedPasswordMessage}
          </Text>
        ) : (
          <ErrorShaker shake={!!errors.password}>
            <Flex
              width='5.5rem'
              justifyContent='space-between'
              mx={{ all: 'auto', md: '5.25rem' }}
            >
              {Array.from({ length: PIN_LENGTH }, (_, index) => (
                <PinIndicator
                  key={index}
                  hasValue={!errors.password && pinCode[index] !== undefined}
                />
              ))}
            </Flex>
          </ErrorShaker>
        )}
        <Flex
          flexDirection='column'
          mt='2rem'
          alignItems={{ all: 'center', md: 'flex-start' }}
        >
          {BUTTONS.map((row) => {
            return (
              <Box key={row.join('')}>
                {row.map((button) => {
                  return button !== BACK_BUTTON_PLACEHOLDER ? (
                    <DialButton
                      key={button}
                      disabled={buttonsDisabled}
                      onClick={() => {
                        if (errors.password) {
                          setPinCode('')
                        }
                        appendPinCode(button)
                      }}
                    >
                      <Text fontSize='2rem'>{button}</Text>
                    </DialButton>
                  ) : (
                    <DialButton
                      key={button}
                      disabled={buttonsDisabled || pinCode === ''}
                      onClick={() => {
                        if (errors.password) {
                          setPinCode('')
                        } else {
                          removePinCodeSymbol()
                        }
                      }}
                    >
                      <Icons.ShevronLeft
                        color={pinCode && !errors.password ? '' : 'white'}
                      />
                    </DialButton>
                  )
                })}
              </Box>
            )
          })}
        </Flex>
      </Flex>
    </Slide>
  )
}
